import { EUserRole } from "@/3514/types"
import { buildCssClassName, getLinkDecorationTwClasses, getTransitionTwClasses } from "@/3514/utils"

const sharedProfileBlockClasses = {
    container: ({ isDropdownOpen }: { isDropdownOpen: boolean }): string =>
        buildCssClassName(
            "relative w-[50px] h-[50px]",
            isDropdownOpen ? "bg-blue-600 rounded-full right-[4px]" : "bg-transparent"
        ),
    listItem: (): string => "py-[10px] border-blue-400 border-b-[1px]",
    mobileListCloser: (): string =>
        buildCssClassName(
            "absolute",
            "right-[30px]",
            "md:right-[20px]",
            "top-[20px]",
            "border-1px",
            "border-transparent",
            "hover:border-gray-23",
            "focus:border-gray-23",
            getTransitionTwClasses("colors", 100)
        ),
    profileListTitle: ({ isMobile }: { isMobile: boolean } = { isMobile: false }): string =>
        buildCssClassName([isMobile && "text-center"], "mt-[10px]"),
    profileList: ({ isMobile }: { isMobile: boolean } = { isMobile: false }): string =>
        buildCssClassName(isMobile && "flex flex-col items-center gap-y-[5px]")
} as const

export default {
    [EUserRole.Coach]: {
        header: (): string =>
            buildCssClassName("w-full", {
                "bg-blue-500": false
            }),
        headerContainer: ({ isJustifyEnd }: { isJustifyEnd: boolean }): string =>
            buildCssClassName(
                "flex",
                "flex-row",
                "items-center",
                "gap-x-[30px]",
                "h-[75px]",
                "pt-[20px]",
                "pb-[20px]",
                "pl-[20px]",
                "px-[20px]",
                "md:pt-[30px]",
                "md:pb-[10px]",
                "md:pl-[40px]",
                "md:px-[35px]",
                {
                    "justify-between": !isJustifyEnd,
                    "justify-end": isJustifyEnd
                }
            ),
        logoBlock: (): string =>
            buildCssClassName(
                "block",
                "outline-transparent",
                "outline",
                "outline-offset-[3px]",
                "outline-[1px]",
                "hover:outline-red-100",
                "focus:outline-red-100",
                getTransitionTwClasses("all", 200)
            ),
        profileBlock: {
            ...sharedProfileBlockClasses,
            opener: ({ isDropdownOpen, isMobile }: { isDropdownOpen: boolean; isMobile: boolean }): string =>
                buildCssClassName(
                    "p-[2px]",
                    "md:p-[5px]",
                    "rounded-full",
                    "border-[5px]",
                    "border-transparent",
                    "hover:border-blue-600",
                    "focus:border-blue-600",
                    [isDropdownOpen ? ["bg-blue-600 z-[11] fixed", isMobile && "hidden"] : "bg-transparent"],
                    getTransitionTwClasses("all", 100)
                ),
            list: (
                { isOpen, isMobile }: { isOpen: boolean; isMobile?: boolean } = {
                    isOpen: false,
                    isMobile: false
                }
            ): string =>
                buildCssClassName(
                    isOpen
                        ? [
                              "flex-col",
                              "flex",
                              "fixed",
                              "py-[15px]",
                              "px-[20px]",
                              "z-[10]",
                              "bg-blue-600",
                              isMobile
                                  ? [
                                        "bottom-0",
                                        "pt-[50px]",
                                        "top-0",
                                        "right-0",
                                        "left-0",
                                        "[width:100vw]",
                                        "[height:100vh]",
                                        "items-center",
                                        "gap-y-[20px]"
                                    ]
                                  : [
                                        "bottom-0",
                                        "top-[35px]",
                                        "[left:auto]",
                                        "right-[30px]",
                                        "rounded-[5px]",
                                        "max-w-[185px]",
                                        "w-full",
                                        "h-min"
                                    ]
                          ]
                        : "hidden"
                )
        }
    },
    [EUserRole.Assistant]: {
        header: (): string => "bg-white border-b-[1px] border-blue-500 w-full",
        loadingValue: ({ isBlurred, intensity }: { isBlurred: boolean; intensity: 3 | 5 }): string =>
            buildCssClassName(
                { "blur-[3px]": isBlurred && intensity === 3 },
                { "blur-[10px]": isBlurred && intensity === 5 },
                getTransitionTwClasses("all,[width]", 200)
            ),
        headerContainer: (): string =>
            "flex items-center justify-between gap-x-[30px] h-[75px] pt-[30px] pb-[10px] pl-[40px] px-[35px]",
        profileBlock: {
            ...sharedProfileBlockClasses,
            opener: ({ isDropdownOpen, isMobile }: { isDropdownOpen: boolean; isMobile: boolean }): string =>
                buildCssClassName(
                    "p-[2px]",
                    "md:p-[5px]",
                    "rounded-full",
                    "border-[5px]",
                    "border-transparent",
                    "hover:border-blue-600",
                    "focus:border-blue-600",
                    [
                        isDropdownOpen
                            ? ["bg-blue-600 z-[11] fixed right-[35px] top-[20px]", isMobile && "hidden"]
                            : "bg-transparent"
                    ],
                    getTransitionTwClasses("all", 100)
                ),
            list: (
                { isOpen, isMobile }: { isOpen: boolean; isMobile?: boolean } = {
                    isOpen: false,
                    isMobile: false
                }
            ): string =>
                buildCssClassName(
                    isOpen
                        ? [
                              "flex-col",
                              "flex",
                              "fixed",
                              "py-[15px]",
                              "px-[20px]",
                              "z-[10]",
                              "bg-blue-600",
                              isMobile
                                  ? [
                                        "bottom-0",
                                        "pt-[50px]",
                                        "top-0",
                                        "right-0",
                                        "left-0",
                                        "[width:100vw]",
                                        "[height:100vh]",
                                        "items-center",
                                        "gap-y-[20px]"
                                    ]
                                  : [
                                        "bottom-0",
                                        "top-[50px]",
                                        "[left:auto]",
                                        "right-[30px]",
                                        "rounded-[5px]",
                                        "max-w-[185px]",
                                        "w-full",
                                        "h-min"
                                    ]
                          ]
                        : "hidden"
                )
        },
        backButton: (): string =>
            buildCssClassName(
                "flex",
                "items-center",
                getLinkDecorationTwClasses({ color: "gray", offset: 3, isDefaultDecorTransparent: true })
            ),
        backButtonText: (): string => "p-[5px]"
    }
}
